/* Mairaz: make the whole Shop Pay Installments card clickable.

   Shopify renders the banner as <shopify-payment-terms> inside form.installment.
   Its contents live in an OPEN shadow root that we do not control: a paragraph
   of copy plus a button, #shopify-installments-cta, that opens Shopify's own
   installments modal. The wording, the amounts, the logo and the modal are
   Shopify's and are never touched here.

   HOW THE WHOLE CARD BECOMES CLICKABLE
   Forwarding a click to that button in script does not work: Shopify only
   responds to a real user click, so button.click() and synthetic pointer and
   mouse events all do nothing. So instead the button's own hit area is grown
   to cover the card. A <style> is injected into the open shadow root giving
   #shopify-installments-cta an ::after that is absolutely positioned and bled
   outwards. A pseudo-element's hit area belongs to its originating element, so
   a click anywhere on the card is a real, trusted click on Shopify's own
   button and the modal opens natively.

   THE BLEED IS MEASURED FROM THE WRAPPER, NOT FROM THE BUTTON.
   This is the whole correctness of the file and it was got wrong once, badly.

   The ::after is absolutely positioned, so its offsets resolve against its
   containing block - the nearest positioned ancestor, which is
   #shopify-installments, the wrapper inside the shadow root. An earlier
   version measured the distance from the BUTTON's edges out to the card's
   edges and then applied those numbers as offsets against the WRAPPER. The
   button sits in the middle of the wrapper, so those distances were far larger
   than the wrapper-to-card gap and the pseudo-element came out 585px wide
   inside a 330px card.

   #shopify-installments has overflow visible, so that excess became real
   scrollable width on the page. On a 360px phone the document measured 461px:
   the whole storefront could be dragged sideways, showing a band of empty
   white with content cut off the left edge. It did not happen in Pakistan only
   because Shop Pay Installments is not offered there, so the banner never
   rendered at all. Reported from a real Android phone on 20 Sep 2026 and
   reproduced at 360x760.

   The correct offsets are the four gaps between the WRAPPER's edges and the
   CARD's edges. Applied as negative offsets they grow the pseudo-element from
   the wrapper out to exactly the card and no further.

   MEASURE AFTER THE READY FLAG, NOT BEFORE.
   Setting data-mrz-installments-ready is what reveals the chevron in
   assets/mairaz-stitching-controls.css, and the chevron is a flex item, so it
   takes width off the banner and moves the wrapper's right edge inwards by
   about 24px. Measuring first and flagging second read the pre-chevron layout
   and left a dead strip down the right-hand side of the card where taps did
   nothing. So the flag goes on first and the measurement is taken after it,
   then taken again on the next two animation frames once that layout has
   settled.

   Belt and braces: assets/mairaz-stitching-controls.css also gives
   form.installment overflow:hidden, so even if this measurement is ever wrong
   again the excess is clipped at the card and can never reach the page. Do not
   remove that rule.

   If Shopify ever renames or restructures the shadow content, nothing here
   matches, the ready flag is not set, the card drops back to a plain frame and
   the banner still works through its own button.
*/
(function () {
  if (window.MairazInstallmentsCard) return;
  window.MairazInstallmentsCard = true;

  var FORM_SELECTOR = 'form.installment';
  var CTA_ID = 'shopify-installments-cta';
  var WRAP_ID = 'shopify-installments';
  var STYLE_ID = 'mrz-installments-hit-area';

  /* No single bleed may exceed this. The gap between the banner wrapper and
     the card is only ever the card's padding plus the chevron, so anything
     larger means the geometry was misread and the page must not pay for it. */
  var MAX_BLEED = 64;

  var HIT_CSS = [
    '#' + WRAP_ID + '{position:relative}',
    '#' + CTA_ID + '::after{',
    'content:"";position:absolute;',
    'top:calc(var(--mrz-inst-bleed-top, 0px) * -1);',
    'bottom:calc(var(--mrz-inst-bleed-bottom, 0px) * -1);',
    'left:calc(var(--mrz-inst-bleed-left, 0px) * -1);',
    'right:calc(var(--mrz-inst-bleed-right, 0px) * -1);',
    '}'
  ].join('');

  function partsFor(form) {
    var host = form.querySelector('shopify-payment-terms');
    if (!host || !host.shadowRoot) return null;
    var button = host.shadowRoot.getElementById(CTA_ID);
    var wrapper = host.shadowRoot.getElementById(WRAP_ID);
    if (!button || !wrapper) return null;
    return { root: host.shadowRoot, button: button, wrapper: wrapper };
  }

  /* Distances from the wrapper (the ::after's containing block) out to the
     card. Negative results mean the wrapper already reaches that edge, so
     nothing is bled on that side. */
  function measure(form, wrapper) {
    var card = form.getBoundingClientRect();
    var box = wrapper.getBoundingClientRect();
    if (!card.width || !box.width) return false;

    var pairs = [
      ['top', box.top - card.top],
      ['bottom', card.bottom - box.bottom],
      ['left', box.left - card.left],
      ['right', card.right - box.right]
    ];

    for (var i = 0; i < pairs.length; i += 1) {
      var value = Math.round(pairs[i][1]);
      if (!isFinite(value) || value < 0) value = 0;
      if (value > MAX_BLEED) value = MAX_BLEED;
      form.style.setProperty('--mrz-inst-bleed-' + pairs[i][0], value + 'px');
    }
    return true;
  }

  function measureSoon(form, wrapper) {
    window.requestAnimationFrame(function () {
      measure(form, wrapper);
      window.requestAnimationFrame(function () { measure(form, wrapper); });
    });
  }

  function ensure(form) {
    var parts = partsFor(form);
    if (!parts) {
      form.removeAttribute('data-mrz-installments-ready');
      return false;
    }

    if (!parts.root.getElementById(STYLE_ID)) {
      var style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = HIT_CSS;
      parts.root.appendChild(style);
    }

    /* Flag first: it reveals the chevron, which changes the geometry we are
       about to read. See MEASURE AFTER THE READY FLAG above. */
    form.setAttribute('data-mrz-installments-ready', 'true');

    if (!measure(form, parts.wrapper)) return false;
    measureSoon(form, parts.wrapper);
    return true;
  }

  function ensureAll(scope) {
    var pending = false;
    (scope || document).querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      if (!ensure(form)) pending = true;
    });
    return pending;
  }

  function remeasureAll() {
    document.querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      var parts = partsFor(form);
      if (parts) measure(form, parts.wrapper);
    });
  }

  function poll() {
    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      var pending = ensureAll(document);
      if (!pending || attempts >= 20) window.clearInterval(timer);
    }, 300);
  }

  function watchResize() {
    var frame = null;
    function schedule() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(function () {
        frame = null;
        remeasureAll();
      });
    }

    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule, { passive: true });

    /* The banner re-flows when Shopify swaps the amount in, and the copy can
       rewrap without the window changing size at all. */
    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(schedule);
      document.querySelectorAll(FORM_SELECTOR).forEach(function (form) {
        observer.observe(form);
        var host = form.querySelector('shopify-payment-terms');
        if (host) observer.observe(host);
      });
      window.__mrzInstallmentsObserver = observer;
    }
  }

  function boot() {
    ensureAll(document);
    poll();
    watchResize();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasureAll);
    window.setTimeout(remeasureAll, 1200);
    window.setTimeout(remeasureAll, 3000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  /* The banner re-renders its amount when the variant changes. */
  document.addEventListener('change', function (event) {
    if (!event.target || !event.target.closest) return;
    if (!event.target.closest('variant-selects')) return;
    window.setTimeout(function () { ensureAll(document); }, 400);
    window.setTimeout(function () { ensureAll(document); }, 1200);
  });

  document.addEventListener('shopify:section:load', function (event) {
    ensureAll(event.target || document);
    poll();
    watchResize();
  });
})();
